/**
 * Centralized icon exports from Heroicons
 *
 * This file maps semantic icon names to Heroicons components for consistent usage
 * across the application. All icons are imported from @heroicons/react/24/outline
 * for a consistent outline style. Use solid variants where emphasis is needed.
 *
 * Usage:
 *   import { icons } from '@/constants/icons';
 *   <icons.User className="h-6 w-6" />
 *
 * Or direct import:
 *   import { UserIcon } from '@heroicons/react/24/outline';
 *   <UserIcon className="h-6 w-6" />
 */

// Import outline icons (24px) - Primary usage
import {
  UserIcon,
  PaintBrushIcon,
  CreditCardIcon,
  BoltIcon,
  LightBulbIcon,
  CalendarIcon,
  DocumentTextIcon,
  SparklesIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  HomeIcon,
  ClipboardDocumentIcon,
  PhotoIcon,
  UsersIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';

// Import solid icons (24px) - Use for emphasis, badges, or filled states
import {
  UserIcon as UserIconSolid,
  PaintBrushIcon as PaintBrushIconSolid,
  CreditCardIcon as CreditCardIconSolid,
  BoltIcon as BoltIconSolid,
  LightBulbIcon as LightBulbIconSolid,
  CalendarIcon as CalendarIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  SparklesIcon as SparklesIconSolid,
  RocketLaunchIcon as RocketLaunchIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  DevicePhoneMobileIcon as DevicePhoneMobileIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
  XCircleIcon as XCircleIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
} from '@heroicons/react/24/solid';

// Re-export for direct imports
export {
  UserIcon,
  PaintBrushIcon,
  CreditCardIcon,
  BoltIcon,
  LightBulbIcon,
  CalendarIcon,
  DocumentTextIcon,
  SparklesIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  HomeIcon,
  ClipboardDocumentIcon,
  PhotoIcon,
  UsersIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  UserIconSolid,
  PaintBrushIconSolid,
  CreditCardIconSolid,
  BoltIconSolid,
  LightBulbIconSolid,
  CalendarIconSolid,
  DocumentTextIconSolid,
  SparklesIconSolid,
  RocketLaunchIconSolid,
  ChartBarIconSolid,
  DevicePhoneMobileIconSolid,
  ExclamationTriangleIconSolid,
  XCircleIconSolid,
  CheckCircleIconSolid,
};

/**
 * Icon object for convenient access with semantic names
 * Prefer this approach for better readability and easier refactoring
 */
export const icons = {
  // User & Profile
  User: UserIcon,
  UserSolid: UserIconSolid,
  Users: UsersIcon,

  // Design & Creative
  Palette: PaintBrushIcon,
  PaletteSolid: PaintBrushIconSolid,
  Photo: PhotoIcon,

  // Business & Commerce
  CreditCard: CreditCardIcon,
  CreditCardSolid: CreditCardIconSolid,

  // Features & Actions
  Bolt: BoltIcon,
  BoltSolid: BoltIconSolid,
  Lightbulb: LightBulbIcon,
  LightbulbSolid: LightBulbIconSolid,
  Sparkles: SparklesIcon,
  SparklesSolid: SparklesIconSolid,
  Rocket: RocketLaunchIcon,
  RocketSolid: RocketLaunchIconSolid,

  // Content & Documents
  Calendar: CalendarIcon,
  CalendarSolid: CalendarIconSolid,
  Document: DocumentTextIcon,
  DocumentSolid: DocumentTextIconSolid,
  Clipboard: ClipboardDocumentIcon,
  DocumentDuplicate: DocumentDuplicateIcon,

  // Analytics & Data
  ChartBar: ChartBarIcon,
  ChartBarSolid: ChartBarIconSolid,

  // Devices & Platforms
  Mobile: DevicePhoneMobileIcon,
  MobileSolid: DevicePhoneMobileIconSolid,

  // Settings & Config
  Settings: Cog6ToothIcon,

  // Feedback & Status
  Warning: ExclamationTriangleIcon,
  WarningSolid: ExclamationTriangleIconSolid,
  Error: XCircleIcon,
  ErrorSolid: XCircleIconSolid,
  Success: CheckCircleIcon,
  SuccessSolid: CheckCircleIconSolid,
  Info: InformationCircleIcon,

  // Navigation
  Home: HomeIcon,
  ArrowRight: ArrowRightIcon,
  ArrowLeft: ArrowLeftIcon,
  ChevronDown: ChevronDownIcon,
  ChevronUp: ChevronUpIcon,

  // Actions
  Plus: PlusIcon,
  Close: XMarkIcon,
  Trash: TrashIcon,
  Edit: PencilIcon,
  View: EyeIcon,
};

/**
 * Common icon sizes as Tailwind classes
 * Use these for consistency across the app
 */
export const iconSizes = {
  xs: 'h-3 w-3',    // 12px
  sm: 'h-4 w-4',    // 16px
  md: 'h-5 w-5',    // 20px
  lg: 'h-6 w-6',    // 24px (default)
  xl: 'h-8 w-8',    // 32px
  '2xl': 'h-10 w-10', // 40px
  '3xl': 'h-12 w-12', // 48px
};

/**
 * Helper function to get icon size class
 * @param {string} size - Size key from iconSizes
 * @returns {string} Tailwind classes for icon size
 */
export const getIconSize = (size = 'lg') => {
  return iconSizes[size] || iconSizes.lg;
};